import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, MapPin, Users, ChevronRight, ExternalLink, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import PremiumHero, { DecorativeLine } from '../components/PremiumHero';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SpeakerInvitePage = () => {
  const { token } = useParams();
  const [content, setContent] = useState(null);
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchContent();
    fetchSpeakers();
  }, [token]);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/speaker-invite/public/${token}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('This invitation link is not valid.');
        } else if (response.status === 403) {
          setError('This invitation is no longer active.');
        } else {
          setError('Unable to load invitation. Please try again later.');
        }
        return;
      }
      
      const data = await response.json();
      setContent(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching invite content:', err);
      setError('Unable to load invitation. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSpeakers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/speakers?visible_only=true`);
      if (response.ok) {
        const data = await response.json();
        // Sort alphabetically by name
        const sorted = data.sort((a, b) => {
          const nameA = a.name?.toLowerCase() || '';
          const nameB = b.name?.toLowerCase() || '';
          return nameA.localeCompare(nameB);
        });
        setSpeakers(sorted);
      }
    } catch (err) {
      console.error('Error fetching speakers:', err);
    }
  };

  const getStatusBadge = (status) => {
    if (status?.toLowerCase().includes('confirmed')) {
      return 'bg-green-100 text-green-800 border-green-200';
    } else if (status?.toLowerCase().includes('pre-confirmed')) {
      return 'bg-blue-100 text-blue-800 border-blue-200';
    } else if (status?.toLowerCase().includes('schedule permitting')) {
      return 'bg-amber-100 text-amber-800 border-amber-200';
    }
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00457f] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#001a2e] via-[#00457f] to-[#003461] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-2xl p-10 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invitation Not Found</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link to="/">
            <Button className="bg-[#00457f] hover:bg-[#003461]">
              Return to Homepage
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!content) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Header - Premium Design */}
      <section className="relative bg-gradient-to-br from-[#001428] via-[#001f3f] to-[#001a2e] text-white py-16 md:py-24 overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full opacity-5">
            <div className="absolute top-10 left-10 w-64 h-64 border border-amber-500 rounded-full"></div>
            <div className="absolute bottom-10 right-10 w-96 h-96 border border-amber-500 rounded-full"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-amber-500/30 rounded-full"></div>
          </div>
        </div>

        {/* Corner decorations */}
        <div className="absolute top-0 left-0 w-40 h-40 border-l-2 border-t-2 border-amber-500/20"></div>
        <div className="absolute bottom-0 right-0 w-40 h-40 border-r-2 border-b-2 border-amber-500/20"></div>
        
        <div className="max-w-6xl mx-auto px-6 text-center relative z-10">
          {/* WNC Logo */}
          <div className="flex justify-center mb-6">
            <img
              src="/assets/images/wnc-logo.png"
              alt="World Nuclear Congress"
              className="h-40 md:h-56 w-auto object-contain"
            />
          </div>
          
          {/* Overline */}
          <p className="text-amber-400 text-sm font-semibold uppercase tracking-[0.3em] mb-4">
            World Nuclear Congress
          </p>
          
          <h1 
            className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold mb-6 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="invite-hero-title"
          >
            {content.hero_title}
          </h1>
          
          {/* Decorative line */}
          <DecorativeLine className="max-w-md mx-auto mb-6" />
          
          <div className="flex flex-wrap items-center justify-center gap-6 text-lg text-slate-300 mb-4">
            <span className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-400" />
              Coming February 2027
            </span>
            <span className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-amber-400" />
              University of Tennessee, Knoxville
            </span>
          </div>
          
          {content.hero_supporting_line && (
            <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto font-light leading-relaxed">
              {content.hero_supporting_line}
            </p>
          )}
        </div>
      </section>

      {/* Honor Statement - Moved to top */}
      <section className="py-12 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="w-16 h-1 bg-gradient-to-r from-yellow-400 to-amber-500 mx-auto mb-6 rounded-full"></div>
          <p className="text-2xl md:text-3xl lg:text-4xl font-serif text-[#00457f] font-medium leading-relaxed" data-testid="honor-statement">
            {content.honor_statement}
          </p>
          <div className="w-16 h-1 bg-gradient-to-r from-yellow-400 to-amber-500 mx-auto mt-6 rounded-full"></div>
        </div>
      </section>

      {/* Prestige Statement */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-lg md:text-xl text-gray-700 leading-relaxed text-justify" data-testid="prestige-statement">
            {content.prestige_statement}
          </p>
        </div>
      </section>

      {/* What Makes Distinct */}
      <section className="py-16 bg-gradient-to-br from-[#001a2e] via-[#00457f] to-[#003461]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6 text-center">
            {content.distinct_title}
          </h2>
          
          <p className="text-lg text-gray-200 mb-8 text-center max-w-3xl mx-auto">
            {content.distinct_intro}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {content.distinct_points?.map((point, index) => (
              <div 
                key={index}
                className="flex items-start gap-3 p-4 bg-white/10 rounded-lg border-l-4 border-yellow-500"
              >
                <ChevronRight className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <p className="text-gray-100">{point}</p>
              </div>
            ))}
          </div>
          
          {content.distinct_closing && (
            <p className="text-lg text-gray-200 text-center max-w-3xl mx-auto font-medium">
              {content.distinct_closing}
            </p>
          )}
        </div>
      </section>

      {/* Program Highlights */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#00457f] mb-10 text-center">
            {content.program_title}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {content.program_highlights?.map((highlight, index) => (
              <div 
                key={index}
                className="flex items-start gap-3 p-5 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 hover:shadow-md transition-shadow"
              >
                <div className="w-8 h-8 bg-[#00457f] text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">
                  {index + 1}
                </div>
                <p className="text-gray-700 leading-relaxed">{highlight}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Participants Section - Linked to Main Speakers Database */}
      <section className="py-16 bg-gradient-to-br from-[#001a2e] to-[#00457f] text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-2">
              {content.participants_title || "Select Invited & Confirmed Participants"}
            </h2>
            <p className="text-gray-400 text-sm">{content.participants_subtitle || "(Select List – Not Comprehensive)"}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[700px] overflow-y-auto pr-2 custom-scrollbar">
            {speakers.map((speaker) => (
              <div 
                key={speaker.id}
                className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/10 hover:bg-white/15 transition-colors flex items-center gap-4"
              >
                {/* Circular Photo */}
                <div className="w-16 h-16 flex-shrink-0 rounded-full overflow-hidden bg-white/20 border-2 border-white/30">
                  {speaker.image_url ? (
                    <img 
                      src={speaker.image_url} 
                      alt={speaker.name}
                      className="w-full h-full object-cover object-top"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className={`w-full h-full items-center justify-center bg-gradient-to-br from-[#00457f] to-[#001a2e] ${speaker.image_url ? 'hidden' : 'flex'}`}>
                    <span className="text-white font-bold text-lg">
                      {speaker.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </span>
                  </div>
                </div>
                
                {/* Speaker Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-white mb-1 truncate">{speaker.name}</h3>
                  <p className="text-sm text-gray-300 mb-2 line-clamp-2">
                    {speaker.title}{speaker.organization ? `, ${speaker.organization}` : ''}
                  </p>
                  <span className={`inline-block px-2 py-0.5 text-xs rounded-full border ${getStatusBadge(speaker.status)}`}>
                    {speaker.status || 'Invited'}
                  </span>
                </div>
              </div>
            ))}
          </div>
          
          {speakers.length === 0 && (
            <p className="text-center text-gray-400">Speaker announcements coming soon.</p>
          )}
        </div>
      </section>

      {/* Action Buttons with Urgency Message */}
      <section className="py-16 bg-white">
        <div className="max-w-xl mx-auto px-6">
          {/* Urgency content */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Users className="w-4 h-4" />
              Limited Seating
            </div>
            <p className="text-xl md:text-2xl text-gray-800 font-medium" data-testid="urgency-message">
              {content.urgency_message}
            </p>
          </div>
          
          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href={content.program_button_link}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#00457f] text-white text-lg font-bold rounded-full hover:bg-[#003461] transition-colors shadow-lg"
              data-testid="program-button"
            >
              {content.program_button_text}
              <ExternalLink className="w-5 h-5" />
            </a>
            <a 
              href={content.register_button_link}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-yellow-500 to-amber-500 text-white text-lg font-bold rounded-full hover:from-yellow-600 hover:to-amber-600 transition-colors shadow-lg"
              data-testid="register-button"
            >
              {content.register_button_text}
              <ChevronRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Custom scrollbar styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.1);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.3);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255,255,255,0.5);
        }
      `}</style>
    </div>
  );
};

export default SpeakerInvitePage;
